import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore'

import { FirebaseCollections, FirebaseFields, Id } from 'src/types'
import { db } from 'src/configuration-firebase'

import localService from 'src/api/localeService'
import { UserInitializeService } from 'src/api/index'

export class FirebaseFirestoreService extends UserInitializeService {
  private async getDocRef(docName: FirebaseFields, carNumber?: string) {
    const userCollectionName = await this.getUserCollectionName()

    const userDocRef = carNumber
      ? doc(
          db,
          userCollectionName,
          FirebaseCollections.CARS,
          carNumber,
          docName,
        )
      : doc(db, userCollectionName, docName)

    const collectionName = carNumber ? carNumber : userCollectionName

    return { userDocRef, collectionName }
  }

  async getCollectionData<T>(
    docName: FirebaseFields,
    carName?: string,
  ): Promise<T[]> {
    const { userDocRef, collectionName } = await this.getDocRef(
      docName,
      carName,
    )
    const docSnap = await getDoc(userDocRef)

    if (docSnap.exists()) {
      const data = docSnap.data()

      return data[collectionName] || []
    }

    return []
  }

  async addDataToCollection(
    docName: FirebaseFields,
    newData: object,
    carName?: string,
  ): Promise<void> {
    const { userDocRef, collectionName } = await this.getDocRef(
      docName,
      carName,
    )
    const docSnap = await getDoc(userDocRef)

    if (docSnap.exists()) {
      const data = docSnap.data()
      const currentCollection = data[collectionName] || []

      const collectionRef = collection(db, collectionName)
      const newItemId = doc(collectionRef).id + crypto.randomUUID()

      const newDataWithId = { id: newItemId, ...newData }
      const updatedCollection = [newDataWithId, ...currentCollection]

      await updateDoc(userDocRef, {
        [collectionName]: updatedCollection,
      })

      return
    }

    const collectionRef = collection(db, collectionName)
    const newItemId = doc(collectionRef).id + crypto.randomUUID()
    const newDataWithId = { id: newItemId, ...newData }

    await setDoc(userDocRef, {
      [collectionName]: [newDataWithId],
    })
  }

  async updateDataInCollection(
    docName: FirebaseFields,
    itemId: string,
    updatedData: object,
    carName?: string,
  ) {
    const { userDocRef, collectionName } = await this.getDocRef(
      docName,
      carName,
    )
    const docSnap = await getDoc(userDocRef)

    if (docSnap.exists()) {
      const data = docSnap.data()
      const currentCollection = data[collectionName] || []

      const itemIndex = currentCollection.findIndex(
        (item: Id<string>) => item.id === itemId,
      )

      if (itemIndex !== -1) {
        currentCollection[itemIndex] = {
          ...currentCollection[itemIndex],
          ...updatedData,
        }

        await updateDoc(userDocRef, {
          [collectionName]: currentCollection,
        })
      } else {
        throw new Error(localService.translation.fireBaseErrors.idNotExist)
      }
    }
  }

  async deleteDataFromCollection(
    docName: FirebaseFields,
    itemId: string,
    carName?: string,
  ) {
    const { userDocRef, collectionName } = await this.getDocRef(
      docName,
      carName,
    )
    const docSnap = await getDoc(userDocRef)

    if (docSnap.exists()) {
      const data = docSnap.data()
      const currentCollection = data[collectionName] || []

      const itemIndex = currentCollection.findIndex(
        (item: Id<string>) => item.id === itemId,
      )

      if (itemIndex !== -1) {
        currentCollection.splice(itemIndex, 1)

        await updateDoc(userDocRef, {
          [collectionName]: currentCollection,
        })
      } else {
        throw new Error(localService.translation.fireBaseErrors.idNotExist)
      }
    }
  }

  async getItemById<T>(
    docName: FirebaseFields,
    itemId: string,
    carName?: string,
  ): Promise<T | null> {
    const { userDocRef, collectionName } = await this.getDocRef(
      docName,
      carName,
    )
    const docSnap = await getDoc(userDocRef)

    if (docSnap.exists()) {
      const data = docSnap.data()
      const currentCollection = data[collectionName] || []

      return (
        currentCollection.find((item: Id<string>) => item.id === itemId) || null
      )
    }

    return null
  }

  async deleteAllInfoCar(carNumber: string): Promise<void> {
    const userCollectionName = await this.getUserCollectionName()

    const collectionRef = collection(
      db,
      userCollectionName,
      FirebaseCollections.CARS,
      carNumber,
    )

    const snapshot = await getDocs(collectionRef)

    const batch = writeBatch(db)

    snapshot.forEach(docSnap => {
      batch.delete(docSnap.ref)
    })

    await batch.commit()
  }
}

export default new FirebaseFirestoreService()
